<template>
  <PForm
    ref="pForm"
    :show-header="false"
    :primary-cta="formState.edit ? updateButton : {}"
    :secondary-cta="{}"
    body-class="q-px-2x-md"
    @submit.prevent="onSubmit"
  >
    <POSettingForm
      v-if="!formState.edit && user?.basicInfo?.role === 'buyer'"
    />
    <div class="col-12 q-mt-xl">
      <span class="text-md text-weight-medium text-neutral">
        Assign trade commodities/items
      </span>
      <q-separator class="q-mb-xs" />
    </div>
    <div class="row q-col-gutter-xl">
      <div class="col-6">
        <PSelectInput
          v-model="formData.categories"
          :emit-value="false"
          :map-options="false"
          :options="unref(categoryFilter.entities)"
          :options-loading="unref(categoryFilter.loading)"
          help="Product Category links the Vendor to the category they trade in."
          input-debounce="300"
          label="Categories"
          option-label="name"
          option-value="_id"
          placeholder="Select categories"
          multiple
          use-chips
          virtual-scroll-slice-size="25"
          virtual-scroll-item-size="53"
          @virtual-scroll="categoryFilter.onScroll"
          @filter="categoryFilter.onFilter"
          @filter-abort="categoryFilter.onAbortFilter"
          @update:model-value="onUpdateCategory"
        ></PSelectInput>
      </div>
      <div class="col-6">
        <PSelectInput
          v-model="formData.products"
          :emit-value="false"
          :map-options="false"
          :options="unref(productFilter.entities)"
          :options-loading="unref(productFilter.loading)"
          label="Products / Items"
          help="Pick specific Items or select “All” to link all items"
          option-label="name"
          option-value="_id"
          placeholder="Select products"
          multiple
          use-chips
          virtual-scroll-slice-size="25"
          virtual-scroll-item-size="53"
          @virtual-scroll="productFilter.onScroll"
          @filter="productFilter.onFilter"
          @filter-abort="productFilter.onAbortFilter"
        ></PSelectInput>
      </div>
    </div>
    <div class="col-12 q-mt-2x-md">
      <span class="text-md text-weight-medium text-neutral">
        Assign an account owner & Agent
      </span>
      <q-separator class="q-mb-xs" />
    </div>
    <div class="row q-col-gutter-xl">
      <div class="col-6">
        <PSelectInput
          v-model="formData.account_owner"
          :emit-value="false"
          :map-options="false"
          :options="unref(kamFilter.entities)"
          :options-loading="unref(kamFilter.loading)"
          help="If you don’t know it now, you can update it later."
          input-debounce="300"
          label="Account Owner"
          option-label="name"
          option-value="_id"
          placeholder="Select an owner"
          virtual-scroll-slice-size="25"
          virtual-scroll-item-size="53"
          @virtual-scroll="kamFilter.onScroll"
          @filter="kamFilter.onFilter"
          @filter-abort="kamFilter.onAbortFilter"
        >
          <template #option="{ itemProps, opt }">
            <q-item v-bind="itemProps">
              <q-item-section>
                <q-item-label>
                  <span class="text-neutral text-sm">{{ opt.name }}</span>
                </q-item-label>
                <q-item-label v-if="opt.email" caption lines="1">
                  <span class="text-neutral-70">{{ opt.email }}</span>
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </PSelectInput>
      </div>
      <div class="col-6">
        <PSelectInput
          v-model="formData.associated_agent"
          :emit-value="false"
          :map-options="false"
          :options="unref(agentFilter.entities)"
          :options-loading="unref(agentFilter.loading)"
          help="If you don’t know it now, you can update it later."
          input-debounce="300"
          label="Associate Agent"
          option-label="name"
          option-value="_id"
          placeholder="Select an Agent"
          virtual-scroll-slice-size="25"
          virtual-scroll-item-size="53"
          @virtual-scroll="agentFilter.onScroll"
          @filter="agentFilter.onFilter"
          @filter-abort="agentFilter.onAbortFilter"
        >
          <template #option="{ itemProps, opt }">
            <q-item v-bind="itemProps">
              <q-item-section>
                <q-item-label>
                  <span class="text-neutral text-sm">{{ opt.name }}</span>
                </q-item-label>
                <q-item-label v-if="opt.email" caption lines="1">
                  <span class="text-neutral-70">{{ opt.email }}</span>
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </PSelectInput>
      </div>
    </div>
  </PForm>
  <div
    v-if="user?.basicInfo?.role === 'buyer' && formState.edit"
    class="q-px-2x-md q-py-md"
  >
    <div class="col-12 q-mt-2x-md">
      <span class="text-md text-weight-medium text-neutral">
        Demand Broadcast
      </span>
      <q-separator class="q-mb-xs" />
    </div>
    <div class="col">
      <PField
        v-model="formData.demand_broadcast"
        label="Do you want to broadcast demand request daily?"
      >
        <template #control>
          <POptionGroup
            v-model="formData.demand_broadcast"
            color="primary-100"
            size="md"
            keep-color
            :options="[
              { label: 'No', value: false },
              { label: 'Yes', value: true },
            ]"
          />
        </template>
      </PField>
    </div>
    <div class="text-right q-ml-auto">
      <PBtn
        label="Update"
        type="update"
        :fluid="false"
        :loading="isDemandBroadcastUpdating"
        @click="updateDemandBroadcast"
      />
    </div>
  </div>
</template>

<script setup>
import { useQuasar } from "quasar";
import { useFilter } from "src/composables/filter";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import otherDetailsModel from "../models/otherDetailsModel";
import POSettingForm from "./POSettingForm.vue";

const $q = useQuasar();
const pForm = ref(null);

const { formState, user } = storeToRefs(useUserStore());
const { putUser, toggleUserForm } = useUserStore();
const onGoingOnboarding = inject("onGoingOnboarding");

const othersData = computed(() => unref(user).othersInfo);

const { formData, isLoading, submitForm } = useForm({
  formModel: otherDetailsModel(),
  entity: othersData,
  formRef: pForm,
  isForm: formState,
  put: putUser,
  toggleForm: toggleUserForm,
  editMode: unref(onGoingOnboarding),
});

const kamFilter = useFilter({
  entity: "user",
  options: {
    params: {
      roles: "kam",
      isActive: 1,
    },
  },
});

const agentFilter = useFilter({
  entity: "user",
  options: {
    params: {
      roles: "agent",
      isActive: 1,
    },
  },
});
const categoryFilter = useFilter({
  entity: "category",
  options: { params: { isSubCategory: false } },
});

let productFilter = {};

const initProductFilter = () => {
  productFilter = useFilter({
    entity: "product",
    options: {
      params: {
        categories:
          unref(formData)
            ?.categories?.map((category) =>
              category?._id ? category._id : category,
            )
            ?.join(",") || null,
        isActive: 1,
      },
    },
  });
};

initProductFilter();

const onUpdateCategory = () => {
  initProductFilter();
};

const isDemandBroadcastUpdating = ref(false);

const updateDemandBroadcast = async () => {
  isDemandBroadcastUpdating.value = true;

  await putUser({
    demand_broadcast: unref(formData).demand_broadcast,
    role: unref(user)?.basicInfo?.role,
  });

  isDemandBroadcastUpdating.value = false;
};

const onSubmit = async () => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      text: `Are you sure you want to edit these details ?`,
      isFooterVisible: true,
      submittable: true,
      cancellable: true,
      closable: true,
      onConfirm: async () => {
        unref(formData).role = unref(user)?.basicInfo?.role;

        await submitForm(false);
      },
    },
  });
};

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => {
      return {};
    },
  },
});

const emit = defineEmits(["update:modelValue"]);

const computedFormData = computed({
  get: function () {
    return props.modelValue;
  },
  set: function (updatedFormData) {
    emit("update:modelValue", updatedFormData);
  },
});

const updateButton = computed(() => {
  return {
    loading: unref(isLoading),
    label: "Update",
    type: "update",
  };
});

watch(
  formData,
  (data) => {
    computedFormData.value = data;
  },
  {
    immediate: true,
  },
);
</script>
<style scoped>
:deep(.text-xs) {
  min-height: 35px;
}
</style>
